import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Data, RouterStateSnapshot, TitleStrategy } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Title } from "@angular/platform-browser";
import { EbenTaskData } from "./eben-task-data.resolver";
import { EbenProcessInstance } from '@alf-nx-workspace/eben/interfaces';

@Injectable({
  providedIn: 'root'
})
export class EbenTitleStrategy extends TitleStrategy {

  constructor(private title: Title, private translate: TranslateService) {
    super();
  }

  updateTitle(routerSnapshot: RouterStateSnapshot) {
    const title = this.buildTitle(routerSnapshot);
    let translatedTitle = 'BOKUflow';
    if (title) {
      translatedTitle = this.translate.instant(title) + ' - BOKUflow';
    }
    let dialogRoot = this.findOutletBranch(routerSnapshot.root, "dialog");
    const dialogDeep = dialogRoot ? this.deepestPrimary(dialogRoot) : null;
    let data = dialogDeep?.data ?? {};
    if (data) {
      translatedTitle = this.updateFromProvidedData(data, translatedTitle);
    }
    this.title.setTitle(translatedTitle);
  }

  updateFromProvidedData(data: Data, translatedTitle: string) {

    switch (true) {
      case !!data.taskData: {
        let taskData = data.taskData as EbenTaskData;
        let taskName = this.translate.instant(taskData.task.name);
        let workflowName = this.translate.instant(taskData.task.processDefinitionKey?.toUpperCase());
        translatedTitle = [taskName, workflowName, translatedTitle].filter(Boolean).join(' - ');
        break;
      }
      case !!data.processInstance: {
        let processInstance: EbenProcessInstance = data.processInstance;
        let workflowName = this.translate.instant(processInstance.processDefinitionKey.toUpperCase());
        translatedTitle = `${workflowName} #${processInstance.id} -  ${translatedTitle}`;
        break;
      }
    }
    return translatedTitle;
  }

  /**
   * Depth-first search for the first node whose outlet matches `outletName`.
   * This finds the entry point of the named-outlet branch.
   */
  findOutletBranch(route: ActivatedRouteSnapshot, outletName: string): ActivatedRouteSnapshot | null {
    if (route.outlet === outletName) return route;
    for (const child of route.children) {
      const found: ActivatedRouteSnapshot = this.findOutletBranch(child, outletName);
      if (found) {
        return found;
      }
    }
    return null;
  }

  /**
   * From a branch root, walk down its primary chain to the deepest route.
   * Within a named outlet subtree, children typically use the 'primary' outlet.
   */
  deepestPrimary(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    let cur = route;
    let next = cur.firstChild;
    while (next) {
      next = cur.children.find(c => c.outlet === 'primary') ?? cur.firstChild ?? null;
      if (!next) return cur;
      cur = next;
    }
  }
}
